export interface InputTransition {
  at: number;
  type: string;
  pointerId: number;
  action: string | null;
  x?: number;
  y?: number;
}

export interface DiagnosticsSnapshot {
  frameSampleCount: number;
  frameTimes: number[];
  frameStats: { p50: number; p95: number; p99: number };
  inputTransitions: InputTransition[];
  droppedSimulationTimeMs: number;
  errors: Array<{ message: string; stack?: string }>;
}

const FRAME_LIMIT = 600;
const INPUT_LIMIT = 200;
const ERROR_LIMIT = 20;

function pushBounded<T>(items: T[], value: T, limit: number): void {
  items.push(value);
  if (items.length > limit) items.splice(0, items.length - limit);
}

export function nearestRankPercentile(samples: readonly number[], percentile: number): number {
  const finite = samples.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (finite.length === 0) return 0;
  const p = Math.min(1, Math.max(0, percentile));
  const rank = Math.max(1, Math.ceil(p * finite.length));
  return finite[rank - 1] ?? finite[finite.length - 1] ?? 0;
}

export class DiagnosticsService {
  private readonly frameTimes: number[] = [];
  private readonly inputTransitions: InputTransition[] = [];
  private readonly errors: Array<{ message: string; stack?: string }> = [];
  private droppedSimulationTimeMs = 0;

  recordFrame(frameTimeMs: number): void {
    if (!Number.isFinite(frameTimeMs) || frameTimeMs < 0) return;
    pushBounded(this.frameTimes, frameTimeMs, FRAME_LIMIT);
  }

  recordInput(transition: InputTransition): void {
    pushBounded(this.inputTransitions, { ...transition }, INPUT_LIMIT);
  }

  addDroppedSimulationTime(milliseconds: number): void {
    if (Number.isFinite(milliseconds) && milliseconds > 0) {
      this.droppedSimulationTimeMs += milliseconds;
    }
  }

  recordError(error: Error): void {
    const serialized: { message: string; stack?: string } = { message: error.message };
    if (error.stack) serialized.stack = error.stack;
    pushBounded(this.errors, serialized, ERROR_LIMIT);
  }

  snapshot(): DiagnosticsSnapshot {
    return {
      frameSampleCount: this.frameTimes.length,
      frameTimes: [...this.frameTimes],
      frameStats: {
        p50: nearestRankPercentile(this.frameTimes, 0.5),
        p95: nearestRankPercentile(this.frameTimes, 0.95),
        p99: nearestRankPercentile(this.frameTimes, 0.99),
      },
      inputTransitions: this.inputTransitions.map((transition) => ({ ...transition })),
      droppedSimulationTimeMs: this.droppedSimulationTimeMs,
      errors: this.errors.map((error) => ({ ...error })),
    };
  }
}
